/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import React, {useContext, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useIntl} from "react-intl";
import {useConfirm} from "material-ui-confirm";
import {TextField} from "@mui/material";

const useEditScoredVideo = (videoKey) => {
    const {signedInUser} = useContext(FirebaseAuthContext);
    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();

    const history = useHistory();

    const [saveInProgress, setSaveInProgress] = useState(false)
    const [saveAttemptInProgress, setSaveAttemptInProgress] = useState(false)

    // Internationalization for simple strings
    const intl = useIntl();

    // Comment ref for the status change
    const statusComment = useRef('')

    const handleSave = (scoredScene) => {
        setSaveAttemptInProgress(true)
        return checkStatisticalRelevance(scoredScene)
            .then((checkResult) => {
                setSaveAttemptInProgress(false)

                if (checkResult.statisticallyRelevantChange) {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.scoredScene.dialog.save.statisticallyRelevant.title',
                            defaultMessage: 'Statistically relevant change'
                        }),
                        description: intl.formatMessage({
                            id: 'dialog.save.statisticallyRelevant.description',
                            defaultMessage: 'Change invalidates related statistical and sets the status to \'DRAFT\'. Save anyway?'
                        })
                    }).then(() => 'OK').catch(() => null)
                } else {
                    return 'OK'
                }
            })
            .then((clickedButton) => {
                if (clickedButton === 'OK') {
                    return save(scoredScene)
                }
                return null
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.scoredScene.messages.edit.error',
                        defaultMessage: 'Something went wrong while saving the video scene: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setSaveInProgress(false)
                setSaveAttemptInProgress(false)
            })
    }

    const checkStatisticalRelevance = async (scoredScene) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos/' + videoKey + '/scoredScenes/' + scoredScene.key + '/statisticallyRelevant', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                sceneData: getStructuredSceneData(scoredScene)
            })
        })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }
                return data.json();
            })
    }

    const save = async (sceneData) => {
        setSaveInProgress(true)
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/videos/' + videoKey + '/scoredScenes/' + sceneData.key, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                sceneData: getStructuredSceneData(sceneData)
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.scoredScene.messages.edit.success',
                    defaultMessage: 'Video scene saved.'
                }), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
            history.push({pathname: '/admin/videos/' + videoKey + '/edit'})
        })
    }

    const getStructuredSceneData = (sceneData) => {
        return {
            basics: {
                statusInfo: sceneData.statusInfo,
                sceneStart: sceneData.videoRange[0],
                sceneEnd: sceneData.videoRange[1],
                difficulty: sceneData.difficulty,
                mainFocus: sceneData.mainFocus,
                trailingPeriod: sceneData.trailingPeriod,
                athleteLeftPosition: sceneData.athleteLeftPosition,
                blurring: sceneData.blurring
            },
            scoreDefinitions: sceneData.scoreDefinitions
        }
    }

    const handleChangeStatus = async (sceneKey, prevStatus, newStatus) => {
        const idToken = await signedInUser.getIdToken()

        return confirm({
            title: intl.formatMessage({
                id: 'app.scoredScene.edit.appbar.actions.status.confirm.title',
                defaultMessage: 'Confirm status'
            }),
            content: <div>
                <div>
                    {intl.formatMessage({
                        id: 'app.scoredScene.edit.appbar.actions.status.confirm.description',
                        defaultMessage: 'Change status from {prevStatus} to {newStatus}?'
                    }, {
                        prevStatus: intl.formatMessage({
                            id: `app.ids.scoredSceneStatus.${prevStatus}`,
                            defaultMessage: prevStatus
                        }),
                        newStatus: intl.formatMessage({
                            id: `app.ids.scoredSceneStatus.${newStatus}`,
                            defaultMessage: newStatus
                        })
                    })}
                </div>
                <div>
                    {newStatus === 'rejected' && <TextField variant="outlined" inputRef={statusComment}
                                                            required={true}
                                                            id="reg-scoredScene-comment" multiline rows={2} maxRows={4}
                                                            style={{width: "100%"}}
                                                            label={intl.formatMessage({
                                                                id: 'app.scoredScene.edit.appbar.actions.status.comment.label',
                                                                defaultMessage: 'Comment'
                                                            })}/>}
                </div>
            </div>
        }).then(() => {
            return fetch('/reg-main/videos/' + videoKey + '/scoredScenes/' + sceneKey + '/changeStatus', {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    idToken: idToken,
                    statusInfo: {
                        status: newStatus,
                        comment: statusComment.current.value
                    }
                })
            })
        }).then(async (data) => {

            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                const errorMsg = (await data.json()).status
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.scoredScene.messages.edit.status.error',
                        defaultMessage: 'Something went wrong while setting status of video scene: {error}'
                    }, {error: errorMsg}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });

                return;
            }

            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.scoredScene.messages.edit.status.success',
                    defaultMessage: 'Video scene status changed.'
                }), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });

            history.push({pathname: '/admin/videos/' + videoKey + '/edit'})
            return;

        }).catch(() => null) // This catch is effective in case of cancelling the confirm dialog
    }

    const cancel = () => {
        history.push({
            pathname: '/admin/videos/' + videoKey + '/edit'
        })
    }

    return {handleSave, cancel, saveInProgress, saveAttemptInProgress, handleChangeStatus}
}
export default useEditScoredVideo;